'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { MapPin, Warehouse, Sun, Users, ShieldCheck } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { Trans } from '@kit/ui/trans';

import type { LandingPageProps } from '../_lib/landing-page.types';

import './styles.css';

// --- Main Landing Page ---
export default function BPadelLandingPage({ tenant }: LandingPageProps) {
  const accountId = tenant?.id;

  return (
    <div className="bg-[var(--bp-dark)]">
      <HeroSection />
      <AboutSection />
      <VenuesSection />
      <AcademySection />
      <CommunitySection />
      <LocationSection accountId={accountId} />
      <CTASection />
    </div>
  );
}

// --- Hero Section (sticky, z-[1]) ---
function HeroSection() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section
      className="bp-sticky-section bp-hero relative min-h-screen flex items-center justify-center py-32"
      style={{ zIndex: 1 }}
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <Image
          src="/landing-pages/bpadel/images/tournament.jpeg"
          alt="B-Padel tournament action in Jeddah"
          fill
          className="object-cover"
          priority
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--bp-dark)]/70 via-[var(--bp-dark)]/40 to-[var(--bp-dark)]/80" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 text-center -mt-16">
        <div
          className={`transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-4 tracking-tight">
            Growing the game
            <br />
            <span className="text-primary">together</span>
          </h1>

          <p className="text-lg sm:text-xl text-white/70 mb-10 max-w-xl mx-auto">
            Professionals &amp; Community leaders
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Link
              href="/book"
              className="inline-flex items-center justify-center bg-primary text-white font-semibold text-base h-12 px-8 rounded-full hover:bg-primary/90 transition-colors duration-200"
            >
              Book a Court
            </Link>
            <Link
              href="/coaches"
              className="inline-flex items-center justify-center bg-primary text-white font-semibold text-base h-12 px-8 rounded-full hover:bg-primary/90 transition-colors duration-200"
            >
              Book a Coach
            </Link>
            <a
              href="#about"
              className="inline-flex items-center justify-center border-2 border-white/30 text-white font-semibold text-base h-12 px-8 rounded-full hover:bg-white/10 transition-colors duration-200"
            >
              Explore
            </a>
          </div>

          {/* Stats */}
          <div className="flex flex-wrap justify-center gap-8 sm:gap-12">
            <StatItem value="2" label="Venues" />
            <StatItem value="14+" label="Courts" />
            <StatItem value="3+" label="Coaches" />
          </div>

          <div className="mt-8 flex translate-y-6 flex-col items-center gap-3 lg:translate-y-28" data-test="bpadel-sponsor">
            <p className="text-sm text-white/70">
              <Trans i18nKey="landing:bpadel.sponsoredBy" />
            </p>
            <div className="-mx-12 -my-2 rounded-md bg-white px-12 py-2">
              <Image
                src="/landing-pages/bpadel/images/totalenergies.webp"
                alt="TotalEnergies"
                width={645}
                height={477}
                className="h-auto w-32 sm:w-36"
              />
            </div>
          </div>
        </div>
      </div>

    </section>
  );
}

function StatItem({ value, label }: { value: string; label: string }) {
  return (
    <div className="text-center">
      <div className="text-3xl sm:text-4xl font-bold text-primary">{value}</div>
      <div className="text-sm text-white/60 mt-1">{label}</div>
    </div>
  );
}

// --- About Section (sticky, z-[2]) ---
function AboutSection() {
  const ref = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      id="about"
      className="bp-sticky-section min-h-screen flex items-center bg-[var(--bp-dark-section)]"
      style={{ zIndex: 2 }}
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 py-20 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <div
            className={`relative aspect-[4/3] rounded-2xl overflow-hidden transition-all duration-1000 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}
          >
            <Image
              src="/landing-pages/bpadel/images/about-community.jpg"
              alt="B-Padel community"
              fill
              className="object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
          </div>

          {/* Text */}
          <div
            className={`transition-all duration-1000 delay-200 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}
          >
            <span className="text-primary text-sm font-semibold tracking-widest uppercase mb-4 block">
              About B-Padel
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              Where padel and community{' '}
              <span className="text-primary">meets</span>
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mb-8">
              B-Padel brings Spanish padel standards to Jeddah. With 3 professional
              coaches, 2 premium venues, and over 14 courts, we are building
              the largest and most consistent padel academy in the Kingdom.
            </p>
            <p className="text-white/70 text-lg leading-relaxed mb-10">
              From beginners to professionals, our community is driven by a shared
              passion for growing the game together.
            </p>

            {/* Stats Row */}
            <div className="grid grid-cols-3 gap-6">
              <div className="text-center sm:text-left">
                <div className="text-2xl sm:text-3xl font-bold text-primary">2</div>
                <div className="text-sm text-white/50">Venues</div>
              </div>
              <div className="text-center sm:text-left">
                <div className="text-2xl sm:text-3xl font-bold text-primary">14+</div>
                <div className="text-sm text-white/50">Premium Courts</div>
              </div>
              <div className="text-center sm:text-left">
                <div className="text-2xl sm:text-3xl font-bold text-primary">3</div>
                <div className="text-sm text-white/50">Coaches</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// --- Venues Section (sticky, z-[3]) ---
function VenuesSection() {
  const ref = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      id="venues"
      className="bp-sticky-section min-h-screen flex items-center bg-[var(--bp-dark)]"
      style={{ zIndex: 3 }}
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 py-20 lg:py-32 w-full">
        {/* Section Header */}
        <div
          className={`text-center mb-16 transition-all duration-800 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <span className="text-primary text-sm font-semibold tracking-widest uppercase mb-4 block">
            Our Venues
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
            Two Premium Locations
          </h2>
        </div>

        {/* Venue Cards */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Khalidiyyah - Outdoor */}
          <div
            className={`group relative rounded-2xl overflow-hidden transition-all duration-1000 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
          >
            <div className="relative aspect-[16/10]">
              <Image
                src="/landing-pages/bpadel/images/venue-khalidiyyah.jpg"
                alt="B-Padel Khalidiyyah outdoor courts"
                fill
                className="object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

              {/* Badge */}
              <div className="absolute top-4 left-4">
                <span className="inline-flex items-center gap-1.5 bg-primary text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                  <Sun className="w-3.5 h-3.5" />
                  Outdoor
                </span>
              </div>
            </div>

            {/* Card Content */}
            <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3">Khalidiyyah</h3>
              <div className="flex flex-wrap gap-2 mb-4">
                <FeatureTag>1 Central Court</FeatureTag>
                <FeatureTag>8 Premium Courts</FeatureTag>
                <FeatureTag>Pro Shop</FeatureTag>
                <FeatureTag>Showers & Cafeteria</FeatureTag>
              </div>
              <Link
                href="/book"
                className="inline-flex items-center text-primary font-semibold text-sm hover:underline"
              >
                Book Now &rarr;
              </Link>
            </div>
          </div>

          {/* Obhur - Indoor */}
          <div
            className={`group relative rounded-2xl overflow-hidden transition-all duration-1000 delay-200 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
          >
            <div className="relative aspect-[16/10]">
              <Image
                src="/landing-pages/bpadel/images/venue-obhur.jpg"
                alt="B-Padel Obhur indoor courts"
                fill
                className="object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

              {/* Badge */}
              <div className="absolute top-4 left-4">
                <span className="inline-flex items-center gap-1.5 bg-blue-600 text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                  <Warehouse className="w-3.5 h-3.5" />
                  Indoor
                </span>
              </div>
            </div>

            {/* Card Content */}
            <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3">Obhur</h3>
              <div className="flex flex-wrap gap-2 mb-4">
                <FeatureTag>6 Premium Courts</FeatureTag>
                <FeatureTag>Indoor AC</FeatureTag>
                <FeatureTag>Showers & Cafeteria</FeatureTag>
                <FeatureTag>Pro Shop</FeatureTag>
              </div>
              <Link
                href="/book"
                className="inline-flex items-center text-primary font-semibold text-sm hover:underline"
              >
                Book Now &rarr;
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FeatureTag({ children }: { children: React.ReactNode }) {
  return (
    <span className="bg-white/10 text-white/80 text-xs px-3 py-1 rounded-full backdrop-blur-sm">
      {children}
    </span>
  );
}

// --- Academy Section (sticky, z-[4]) ---
function AcademySection() {
  const ref = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      id="academy"
      className="bp-sticky-section min-h-screen flex items-center bg-[var(--bp-dark-section)]"
      style={{ zIndex: 4 }}
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 py-20 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Text */}
          <div
            className={`order-2 lg:order-1 transition-all duration-1000 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}
          >
            <span className="text-primary text-sm font-semibold tracking-widest uppercase mb-4 block">
              Academy
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              Built with Spanish{' '}
              <span className="text-primary">Padel standards</span>
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mb-8">
              Our academy has 3 professional coaches, the largest and most
              consistent coaching team in the Kingdom. From kids to adults, beginners to
              advanced players, we have programs to help you improve.
            </p>

            {/* Coach Highlight */}
            <div className="bg-white/5 rounded-xl p-6 border border-white/10 mb-8">
              <div className="flex items-center gap-4">
                <div className="relative w-16 h-16 rounded-full overflow-hidden flex-shrink-0">
                  <Image
                    src="/landing-pages/bpadel/images/coach.jpeg"
                    alt="B-Padel Spanish coach"
                    fill
                    className="object-cover"
                  />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <ShieldCheck className="w-4 h-4 text-primary" />
                    <span className="text-white font-semibold text-sm">Professional Coaches</span>
                  </div>
                  <p className="text-white/50 text-sm">
                    Certified professionals with international tournament experience
                  </p>
                </div>
              </div>
            </div>

            <Link
              href="/book"
              className="inline-flex items-center justify-center bg-primary text-white font-semibold text-base h-12 px-8 rounded-full hover:bg-primary/90 transition-colors duration-200"
            >
              Book a Lesson
            </Link>
          </div>

          {/* Images */}
          <div
            className={`order-1 lg:order-2 grid grid-cols-2 gap-4 transition-all duration-1000 delay-200 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}
          >
            <div className="relative aspect-[3/4] rounded-2xl overflow-hidden">
              <Image
                src="/landing-pages/bpadel/images/academy-training.jpg"
                alt="B-Padel academy training"
                fill
                className="object-cover"
              />
            </div>
            <div className="relative aspect-[3/4] rounded-2xl overflow-hidden mt-8">
              <Image
                src="/landing-pages/bpadel/images/kids-academy.jpeg"
                alt="B-Padel kids academy"
                fill
                className="object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// --- Community Section (sticky, z-[5]) ---
function CommunitySection() {
  const ref = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className="bp-sticky-section relative min-h-screen flex items-center bg-[var(--bp-dark)]"
      style={{ zIndex: 5 }}
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <Image
          src="/landing-pages/bpadel/images/community.jpg"
          alt="B-Padel FIP Rise Jeddah community group photo"
          fill
          className="object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--bp-dark)] via-[var(--bp-dark)]/80 to-[var(--bp-dark)]/40" />
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 py-20 lg:py-32">
        <div
          className={`max-w-xl transition-all duration-1000 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <span className="text-primary text-sm font-semibold tracking-widest uppercase mb-4 block">
            Community
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
            From young to{' '}
            <span className="text-primary">professional</span> players
          </h2>
          <p className="text-white/70 text-lg leading-relaxed mb-8">
            B-Padel is more than courts. It&#39;s a community. We host FIP Rise tournaments,
            leagues, social events, and junior programs. Whether you&#39;re picking up a racket
            for the first time or competing at the highest level, you belong here.
          </p>

          <div className="flex flex-wrap gap-4">
            <Link
              href="/book"
              className="inline-flex items-center justify-center bg-primary text-white font-semibold text-base h-12 px-8 rounded-full hover:bg-primary/90 transition-colors duration-200"
            >
              Join the Community
            </Link>
            <a
              href="https://www.instagram.com/bpadel.sa"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center border-2 border-white/30 text-white font-semibold text-base h-12 px-8 rounded-full hover:bg-white/10 transition-colors duration-200"
            >
              Follow Us
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

// --- Location Section (NOT sticky - normal flow) ---
function LocationSection({ accountId }: { accountId?: string }) {
  const supabase = useSupabase();
  const { data: locations } = useQuery({
    queryKey: ['bpadel-locations', accountId],
    queryFn: async () => {
      if (!accountId) return [];

      const { data, error } = await supabase.rpc('get_public_locations', {
        p_account_id: accountId,
      });

      if (error || !data) return [];
      return data;
    },
    enabled: !!accountId,
  });

  const ref = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      id="location-section"
      className="relative bg-[var(--bp-dark-section)] py-20 lg:py-32"
      style={{ zIndex: 6 }}
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12">
        {/* Section Header */}
        <div
          className={`text-center mb-16 transition-all duration-800 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <span className="text-primary text-sm font-semibold tracking-widest uppercase mb-4 block">
            Find Us
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
            Our Locations
          </h2>
        </div>

        {/* Location Cards */}
        {locations && locations.length > 0 ? (
          <div className="grid md:grid-cols-2 gap-8">
            {locations.map((location) => (
              <div
                key={location.id}
                className={`bg-white/5 rounded-2xl border border-white/10 overflow-hidden transition-all duration-800 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              >
                {/* Map */}
                {(location.google_maps_embed_url || location.google_maps_url) && (
                  <LazyMap
                    src={location.google_maps_embed_url || getEmbedUrl(location.google_maps_url ?? '', location.address, location.name)}
                    title={`${location.name} map`}
                  />
                )}

                {/* Info */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-2">{location.name}</h3>
                  {location.address && (
                    <div className="flex items-start gap-2 text-white/60 text-sm mb-2">
                      <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary" />
                      <span>{location.address}</span>
                    </div>
                  )}
                  {location.city && (
                    <p className="text-white/40 text-sm ml-6">{location.city}</p>
                  )}
                  {location.google_maps_url && (
                    <a
                      href={location.google_maps_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-primary text-white text-sm font-semibold mt-4 px-5 py-2.5 rounded-full hover:bg-primary/90 transition-colors duration-200"
                    >
                      <MapPin className="w-4 h-4" />
                      Get Directions
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Fallback when no DB data */
          <div className="grid md:grid-cols-2 gap-8">
            <LocationFallbackCard
              name="Khalidiyyah"
              description="Outdoor venue with 9 premium courts"
              city="Jeddah, Saudi Arabia"
            />
            <LocationFallbackCard
              name="Obhur"
              description="Indoor AC venue with 6 premium courts"
              city="Jeddah, Saudi Arabia"
            />
          </div>
        )}
      </div>
    </section>
  );
}

function LocationFallbackCard({ name, description, city }: { name: string; description: string; city: string }) {
  return (
    <div className="bg-white/5 rounded-2xl border border-white/10 p-6">
      <h3 className="text-xl font-bold text-white mb-2">{name}</h3>
      <p className="text-white/60 text-sm mb-2">{description}</p>
      <div className="flex items-center gap-2 text-white/40 text-sm">
        <MapPin className="w-4 h-4 text-primary" />
        <span>{city}</span>
      </div>
    </div>
  );
}

// --- Lazy Map Component ---
function LazyMap({ src, title }: { src: string; title: string }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = mapRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsLoaded(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={mapRef} className="aspect-[16/9] bg-white/5">
      {isLoaded ? (
        <iframe
          src={src}
          title={title}
          className="w-full h-full border-0"
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-white/30">
          <MapPin className="w-8 h-8" />
        </div>
      )}
    </div>
  );
}

function getEmbedUrl(googleMapsUrl: string, address?: string | null, name?: string | null): string {
  try {
    // Already an embed URL (pb= format or /embed path)
    if (googleMapsUrl.includes('/embed') || googleMapsUrl.includes('output=embed')) {
      return googleMapsUrl;
    }

    // Use business name + address for the embed query - pinpoints the exact location
    if (name && address) {
      return `https://www.google.com/maps?q=${encodeURIComponent(`${name} ${address}`)}&output=embed`;
    }
    if (address) {
      return `https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`;
    }

    // Extract place name from /place/ URLs
    const placeMatch = googleMapsUrl.match(/\/place\/([^/?]+)/);
    if (placeMatch?.[1]) {
      const query = decodeURIComponent(placeMatch[1].replace(/\+/g, ' '));
      return `https://www.google.com/maps?q=${encodeURIComponent(query)}&output=embed`;
    }

    // Extract coordinates from @lat,lng URLs
    const coordMatch = googleMapsUrl.match(/@(-?[\d.]+),(-?[\d.]+)/);
    if (coordMatch?.[1] && coordMatch?.[2]) {
      return `https://www.google.com/maps?q=${coordMatch[1]},${coordMatch[2]}&output=embed`;
    }

    // Fallback: embed the URL directly as a query
    return `https://www.google.com/maps?q=${encodeURIComponent(googleMapsUrl)}&output=embed`;
  } catch {
    return googleMapsUrl;
  }
}

// --- CTA Section (NOT sticky - final) ---
function CTASection() {
  const ref = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className="relative py-24 lg:py-32 overflow-hidden"
      style={{ zIndex: 7 }}
    >
      <div className="absolute inset-0 bg-primary" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 text-center">
        <div
          className={`transition-all duration-1000 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <Users className="w-12 h-12 text-white/80 mx-auto mb-6" />
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Play?
          </h2>
          <p className="text-white/80 text-lg max-w-lg mx-auto mb-10">
            Book a court, join the academy, or become part of our growing community.
          </p>
          <Link
            href="/book"
            className="inline-flex items-center justify-center bg-white text-[var(--bp-dark)] font-bold text-base h-14 px-10 rounded-full hover:bg-white/90 transition-colors duration-200"
          >
            Book Now
          </Link>
        </div>
      </div>
    </section>
  );
}
